import { updateConversation, updateConversationTitle, } from '../redux/assistantConversationSlice';
import { addMessage, appendMessageContent, appendSSEEvent, replaceLatestTempUserMessageId, } from '../redux/assistantMessageSlice';
import { clearConversationBlockingError, clearMessageStreamingError, setAssistantActiveToolCall, setAssistantStatus, setConversationBlockingError, setMessageStreamingError, setModelAutoSwitch, } from '../redux/assistantSlice';
import { AssistantStatus, AssistantTranslationKeys, } from '../constants/assistant.constants';
import { ActorType } from '../types/conversation.types';
import { MessageRole } from '../types/message.types';
import { MODEL_FALLBACK_REASON_PROVIDER_ERROR, SSEEventPayloadType, } from '../types/sse.types';
import { isAgentControlTool } from '../utils/sseEventUtils';
export class EventProcessor {
    constructor(dispatch, conversationId) {
        this.streamingMessageId = null;
        this.activeToolMessageId = null;
        this.lastEventTimestamp = 0;
        this.pendingContinuation = false;
        this.onContinuationCallback = null;
        this.onTurnRejectedCallback = null;
        this.continuationCount = 0;
        this.continuationTimeoutId = null;
        this.dispatch = dispatch;
        this.currentConversationId = conversationId;
    }
    setOnContinuation(callback) {
        this.onContinuationCallback = callback;
    }
    setOnTurnRejected(callback) {
        this.onTurnRejectedCallback = callback;
    }
    clearScheduledContinuation() {
        if (this.continuationTimeoutId !== null) {
            clearTimeout(this.continuationTimeoutId);
            this.continuationTimeoutId = null;
        }
    }
    resetStreamingAndContinuationState() {
        this.clearScheduledContinuation();
        this.pendingContinuation = false;
        this.continuationCount = 0;
        this.streamingMessageId = null;
        this.activeToolMessageId = null;
    }
    processSSEEvent(event) {
        this.lastEventTimestamp = Date.now();
        try {
            const toolName = this.getToolNameFromEvent(event);
            if (toolName === 'respond_to_user_and_continue') {
                this.pendingContinuation = true;
            }
            if (isAgentControlTool(toolName)) {
                return;
            }
            const shouldSkipOrphanTerminalAppend = (event.type === SSEEventPayloadType.ASSISTANT_COMPLETE ||
                event.type === SSEEventPayloadType.ASSISTANT_ERROR) &&
                this.streamingMessageId === null &&
                !!event.message_uuid;
            switch (event.type) {
                case SSEEventPayloadType.ASSISTANT_STATUS:
                    this.handleAssistantStatus(event);
                    break;
                case SSEEventPayloadType.ASSISTANT_MESSAGE_CHUNK:
                    this.handleAssistantMessageChunk(event);
                    break;
                case SSEEventPayloadType.ASSISTANT_TOOL_USE_START:
                    this.handleAssistantToolUseStart(event);
                    break;
                case SSEEventPayloadType.ASSISTANT_TOOL_PROGRESS:
                    this.handleAssistantToolProgress(event);
                    break;
                case SSEEventPayloadType.ASSISTANT_TOOL_RESULT:
                    this.handleAssistantToolResult(event);
                    break;
                case SSEEventPayloadType.ASSISTANT_TOOL_USE_END:
                    this.handleAssistantToolUseEnd(event);
                    break;
                case SSEEventPayloadType.ASSISTANT_COMPLETE:
                    this.handleAssistantComplete(event);
                    break;
                case SSEEventPayloadType.ASSISTANT_ERROR:
                    this.handleAssistantError(event);
                    break;
                case SSEEventPayloadType.ASSISTANT_WARNING:
                    this.handleAssistantWarning(event.payload);
                    break;
                case SSEEventPayloadType.ASSISTANT_REPLY_MODEL_FALLBACK:
                    this.handleReplyModelFallback(event);
                    break;
                case SSEEventPayloadType.USER_MESSAGE_CREATED:
                    this.handleUserMessage(event);
                    break;
                case SSEEventPayloadType.CONVERSATION_TITLE_UPDATED:
                    this.handleConversationTitleUpdated(event);
                    break;
                default:
                    console.warn('[EventProcessor] Unknown SSE event type:', event.type);
            }
            const shouldAppendSSEEventToMessageMeta = event.message_uuid &&
                event.message_uuid !== '' &&
                event.type !== SSEEventPayloadType.CONVERSATION_TITLE_UPDATED &&
                !shouldSkipOrphanTerminalAppend;
            if (shouldAppendSSEEventToMessageMeta) {
                this.dispatch(appendSSEEvent({
                    messageId: event.message_uuid,
                    sseEvent: event,
                }));
            }
        }
        catch (error) {
            console.error('[EventProcessor] Error processing SSE event:', {
                error,
                message: error instanceof Error ? error.message : String(error),
                stack: error instanceof Error ? error.stack : undefined,
                event: event.type,
            });
            this.dispatch(setAssistantStatus(AssistantStatus.ERROR));
            this.dispatch(setConversationBlockingError(error instanceof Error ? error.message : String(error)));
        }
    }
    handleAssistantStatus(event) {
        if (!event.payload.status_key)
            return;
        this.dispatch(setAssistantStatus(event.payload.status_key));
    }
    handleAssistantMessageChunk(event) {
        const payload = event.payload;
        const messageUuid = event.message_uuid;
        if (!payload.content)
            return;
        const convId = event.conversation_id || this.currentConversationId;
        if (!convId) {
            console.warn('[EventProcessor] ASSISTANT_MESSAGE_CHUNK with no conversation id');
            return;
        }
        if (!this.streamingMessageId || this.streamingMessageId !== messageUuid) {
            const message = {
                id: messageUuid,
                conversation_id: convId,
                root_conversation_id: convId,
                role: MessageRole.ASSISTANT,
                author_id: payload.model || 'assistant',
                author_type: ActorType.SYSTEM_PROCESS,
                content: payload.content,
                timestamp: new Date().toISOString(),
            };
            this.streamingMessageId = messageUuid;
            this.dispatch(addMessage(message));
            this.dispatch(setAssistantStatus(AssistantStatus.RESPONDING));
        }
        else {
            this.dispatch(appendMessageContent({
                messageCompositeID: this.streamingMessageId,
                content: payload.content,
            }));
        }
    }
    handleAssistantToolUseStart(event) {
        const payload = event.payload;
        const messageUuid = event.message_uuid;
        const toolCallData = payload.tool_call_data;
        if (!toolCallData) {
            console.error('[EventProcessor] Missing tool_call_data in payload');
            return;
        }
        const convId = event.conversation_id || this.currentConversationId;
        if (!convId) {
            console.warn('[EventProcessor] ASSISTANT_TOOL_USE_START with no conversation id');
            return;
        }
        const toolMessage = {
            id: messageUuid,
            conversation_id: convId,
            root_conversation_id: convId,
            role: MessageRole.TOOL,
            author_id: toolCallData.function.name,
            author_type: ActorType.TOOL,
            content: '',
            timestamp: new Date().toISOString(),
            meta: {
                tool_calls: [toolCallData],
            },
        };
        this.activeToolMessageId = messageUuid;
        this.dispatch(addMessage(toolMessage));
        this.dispatch(setAssistantStatus(AssistantStatus.TOOL_EXECUTING));
        this.dispatch(setAssistantActiveToolCall(toolCallData));
    }
    handleAssistantToolProgress(event) {
        const payload = event.payload;
        const progressData = payload.tool_progress_data;
        if (!progressData)
            return;
        if (isAgentControlTool(progressData.tool_name))
            return;
    }
    handleAssistantToolResult(event) {
        var _a;
        const messageUuid = event.message_uuid;
        const payload = event.payload;
        const resultData = payload.tool_result_data;
        if (!resultData) {
            console.error('[EventProcessor] Missing tool_result_data in payload');
            return;
        }
        if (isAgentControlTool(resultData.tool_name))
            return;
        this.dispatch(appendMessageContent({
            messageCompositeID: messageUuid,
            content: resultData.content || '',
        }));
        if (resultData.tool_name === 'set_agent_mode' && resultData.content) {
            try {
                const parsed = JSON.parse(resultData.content);
                if (parsed.success && this.currentConversationId) {
                    this.dispatch(updateConversation({
                        id: this.currentConversationId,
                        agent_mode: (_a = parsed.current_mode) !== null && _a !== void 0 ? _a : null,
                    }));
                }
            }
            catch (_b) {
                console.error('[EventProcessor] Failed to sync agent_mode from tool result');
            }
        }
        this.dispatch(setAssistantActiveToolCall(null));
    }
    handleAssistantToolUseEnd(event) {
        var _a;
        const payload = event.payload;
        if (isAgentControlTool((_a = payload.tool_result_data) === null || _a === void 0 ? void 0 : _a.tool_name))
            return;
        this.activeToolMessageId = null;
        this.dispatch(setAssistantStatus(AssistantStatus.RESPONDING));
    }
    handleAssistantComplete(event) {
        const completeMessageUuid = event.message_uuid;
        if (this.streamingMessageId === null &&
            !!completeMessageUuid &&
            !this.pendingContinuation) {
            this.dispatch(setMessageStreamingError({
                message: 'assistant.error_generic_failure',
                type: 'connection',
                timestamp: new Date().toISOString(),
                dismissible: true,
            }));
            this.dispatch(setAssistantStatus(AssistantStatus.COMPLETE));
            this.dispatch(clearConversationBlockingError());
            return;
        }
        this.streamingMessageId = null;
        this.activeToolMessageId = null;
        if (this.pendingContinuation && this.onContinuationCallback) {
            this.pendingContinuation = false;
            if (this.continuationCount >= EventProcessor.MAX_CONTINUATION_DEPTH) {
                console.warn(`[EventProcessor] Max continuation depth (${EventProcessor.MAX_CONTINUATION_DEPTH}) reached, stopping auto-continuation`);
                this.continuationCount = 0;
                this.dispatch(setAssistantStatus(AssistantStatus.COMPLETE));
                this.dispatch(clearConversationBlockingError());
                this.dispatch(clearMessageStreamingError());
                return;
            }
            this.continuationCount++;
            this.dispatch(setAssistantStatus(AssistantStatus.RESPONDING));
            this.dispatch(clearConversationBlockingError());
            this.dispatch(clearMessageStreamingError());
            this.clearScheduledContinuation();
            const continuationCallback = this.onContinuationCallback;
            this.continuationTimeoutId = setTimeout(() => {
                this.continuationTimeoutId = null;
                if (!continuationCallback) {
                    return;
                }
                void Promise.resolve()
                    .then(() => continuationCallback())
                    .catch((error) => {
                    console.error('[EventProcessor] Continuation callback failed:', error);
                    this.handleContinuationFailure(error);
                });
            }, 0);
            return;
        }
        this.clearScheduledContinuation();
        this.pendingContinuation = false;
        this.continuationCount = 0;
        this.dispatch(setAssistantStatus(AssistantStatus.COMPLETE));
        this.dispatch(clearConversationBlockingError());
        this.dispatch(clearMessageStreamingError());
    }
    handleContinuationFailure(error) {
        this.resetStreamingAndContinuationState();
        this.dispatch(clearConversationBlockingError());
        this.dispatch(setMessageStreamingError({
            message: error instanceof Error
                ? error.message
                : 'Failed to continue assistant response.',
            type: 'connection',
            timestamp: new Date().toISOString(),
            dismissible: true,
        }));
        this.dispatch(setAssistantStatus(AssistantStatus.COMPLETE));
    }
    handleAssistantError(event) {
        const payload = event.payload;
        const errorMessage = payload.status_key && payload.status_key.startsWith('assistant.')
            ? payload.status_key
            : payload.error || 'An unknown error occurred';
        const errorType = payload.status_key;
        const hasMessageScope = !!(event.message_uuid || payload.message_uuid);
        const isTurnLockRejection = errorType === AssistantTranslationKeys.TurnInProgress;
        if (isTurnLockRejection ||
            errorType === AssistantTranslationKeys.ContinuationNotAllowed) {
            this.resetStreamingAndContinuationState();
            this.dispatch(clearMessageStreamingError());
            this.dispatch(clearConversationBlockingError());
            this.dispatch(setAssistantStatus(AssistantStatus.COMPLETE));
            if (isTurnLockRejection && this.onTurnRejectedCallback) {
                this.onTurnRejectedCallback();
            }
            return;
        }
        if (errorType === 'assistant.connection_error' ||
            errorType === 'assistant.rate_limit' ||
            errorType === 'assistant.model_error' ||
            (errorType === 'assistant.error_generic_failure' && hasMessageScope)) {
            this.dispatch(setMessageStreamingError({
                message: errorMessage,
                type: 'connection',
                timestamp: new Date().toISOString(),
                dismissible: true,
            }));
            this.dispatch(setAssistantStatus(AssistantStatus.COMPLETE));
            this.dispatch(clearConversationBlockingError());
        }
        else {
            this.dispatch(setConversationBlockingError(errorMessage));
            this.dispatch(setAssistantStatus(AssistantStatus.ERROR));
        }
        this.resetStreamingAndContinuationState();
    }
    handleAssistantWarning(payload) {
        const warningMessage = payload.content || 'A warning occurred';
        console.warn('[EventProcessor] Assistant warning:', {
            statusKey: payload.status_key,
            message: warningMessage,
            timestamp: new Date().toISOString(),
        });
    }
    handleReplyModelFallback(event) {
        const { model, previous_model, fallback_reason } = event.payload;
        if (!model)
            return;
        this.dispatch(setModelAutoSwitch({
            fromModel: previous_model || '',
            toModel: model,
            reason: fallback_reason || MODEL_FALLBACK_REASON_PROVIDER_ERROR,
        }));
    }
    handleUserMessage(event) {
        const convId = event.conversation_id || this.currentConversationId;
        if (!convId) {
            console.warn('[EventProcessor] USER_MESSAGE_CREATED with no conversation id');
            return;
        }
        this.dispatch(replaceLatestTempUserMessageId({
            conversationId: convId,
            newId: event.message_uuid,
        }));
    }
    handleConversationTitleUpdated(event) {
        if (!event.payload.content) {
            console.warn('[EventProcessor] Missing title content in CONVERSATION_TITLE_UPDATED event');
            return;
        }
        const conversationId = event.conversation_id || this.currentConversationId;
        if (!conversationId) {
            console.warn('[EventProcessor] No conversation ID available for title update');
            return;
        }
        this.dispatch(updateConversationTitle({
            conversationId: conversationId,
            title: event.payload.content,
        }));
    }
    getToolNameFromEvent(event) {
        var _a, _b, _c, _d;
        const p = event.payload;
        switch (event.type) {
            case 'assistant_tool_use_start':
                return (_b = (_a = p.tool_call_data) === null || _a === void 0 ? void 0 : _a.function) === null || _b === void 0 ? void 0 : _b.name;
            case 'assistant_tool_progress':
                return (_c = p.tool_progress_data) === null || _c === void 0 ? void 0 : _c.tool_name;
            case 'assistant_tool_result':
            case 'assistant_tool_use_end':
                return (_d = p.tool_result_data) === null || _d === void 0 ? void 0 : _d.tool_name;
            default:
                return undefined;
        }
    }
    getActiveToolMessageId() {
        return this.activeToolMessageId;
    }
    getLastEventTimestamp() {
        return this.lastEventTimestamp;
    }
    reset() {
        this.streamingMessageId = null;
        this.activeToolMessageId = null;
        this.lastEventTimestamp = 0;
        this.clearScheduledContinuation();
        this.pendingContinuation = false;
        this.continuationCount = 0;
    }
    processEvent(event) {
        if (event.type && event.payload) {
            const sseEvent = {
                type: event.type,
                message_uuid: event.message_uuid || '',
                payload: event.payload,
            };
            this.processSSEEvent(sseEvent);
        }
        else {
            console.warn('[EventProcessor] Received legacy event format:', event);
        }
    }
}
EventProcessor.MAX_CONTINUATION_DEPTH = 5;
